<template>
  <el-form :ref="thisRef" class="file-details" label-position="left" inline>

    <div v-if="!fileData.url">

      <h4>Please upload a file</h4><br />
      <!-- XOL-526 (D6): deleting the Category slot itself is Tower-Co-only —
           the Telco loses this button (it kept it, unintentionally, before). -->
      <!-- XOL-596 (completed steps): a completed step's categories are read-only, so
           Remove Category is removed from the DOM (a disabled button's tooltip can't be
           reached). -->
      <span v-if="fileData.updated_by && isTowerCo() && !uploadLocked">&nbsp;&nbsp;
        <el-button type="text" @click="deleteFile()">
        <i class="fa fa-trash-alt text-danger">&nbsp;</i>Remove Category
      </el-button>
    </span>

      <div v-if="fileData.updated_by">
        Updated by: <b>{{fileData.updated_by}}</b> on <i><b>{{formatDateTime(this.fileData.updated_at)}}</b></i>
      </div>
    </div>

    <div v-else>

      <el-row :gutter="8">
        <!-- XOL-606: at xs / sm / md (vue-mq keys are upper bounds: a 375px phone is `sm`) the
             icon column is wider, 6 spans instead of 3: in edit mode it holds two icon buttons
             (about 38px) and the 3-span column is about 26px at 375px, so the X dropped under the
             check. The icon buttons carry accessible names. 1440px is unchanged. -->
        <el-col :span="isMediumOrLess ? 18 : 21">
          <text-input style="display: block" label="Category"  v-model="fileData.title" :disabled="!editTitle" />
        </el-col>

        <el-col :span="isMediumOrLess ? 6 : 3">
            <el-button
              v-if="!this.fileData.approved && this.isManager() && this.isTowerCo() && !uploadLocked"
              type="text"
              plain
              :icon="editTitle ? 'fa fa-check-circle' : 'fa fa-edit'"
              :aria-label="editTitle ? 'Save category' : 'Edit category'"
              @click="edit"
              :disabled="isViewOnly()">
            </el-button>

            <el-button
              v-if="editTitle"
                type="text"
                plain
                icon="fa fa-times-circle"
                aria-label="Cancel editing category"
                @click="editTitle = false">
              </el-button>

        </el-col>

        <el-col :span="24">
          <el-form-item label="File name">
            <b class="file-name">{{fileData.name}}</b> (<i>{{this.formatInteger(fileData.size / 1000)}}k</i>)&nbsp;&nbsp;

            <!-- XOL-526 (D6, UX §1.5): a non-Tower-Co viewer may only remove
                 its OWN org's undownloaded upload, and only while the party-1
                 step is still open — the API refuses a counterparty removal
                 on a locked step. Note the gate hides the button; a disabled
                 button's tooltip can't be reached. No new note copy here:
                 the step-lock alert (U-G) already explains the lock.
                 XOL-596 (Carlos, 2026-10-14): a COMPLETED step's files are read-only
                 for the Tower Co too, so the lock now hides it for every viewer
                 (the files note under the table says why). -->
            <el-button
              v-if="!fileData.approved && (!this.fileData.downloaded || this.isManager() || this.isSuper()) && (isTowerCo() || uploadedByMyOrg) && !uploadLocked"
                type="danger"
                size="small"
                plain
                icon="fa fa-trash-alt"
                :aria-label="'Remove ' + fileData.name"
                @click="remove"
                :disabled="isViewOnly() || disableForm">
            </el-button>

            <small v-if="removeNote" class="text-muted">{{removeNote}}</small>

          </el-form-item>
        </el-col>
      </el-row>

      <el-row align="middle">
        <span v-if="!fileData.approved">Approval Status: </span>
        <el-switch v-model="fileData.approved" v-if="(this.isManager() || this.isApprover()) && showSwitch"
        active-color="#13ce66" inactive-color="#E6A23C" @change="approved" :disabled="isViewOnly() || disableForm || uploadLocked"></el-switch>

        <b v-if="fileData.approved" class="text-success" ><i class="fa fa-check-circle">&nbsp;</i>Approved</b>
        <span class="text-warning" v-else><i class="fa fa-question-circle">&nbsp;</i>Pending</span>

        <span v-if="fileData.approved"><small>On <b>{{this.formatDateTime(fileData.approved_at)}}</b> by <b>{{fileData.approved_by}}</b></small></span>
      </el-row>

      <div v-if="hasDetailFields" class="file-info-pane">
        <edit-pane
          title="Additional Info"
          title-heading="h4"
          :editable="canEditInfo"
          :is-edit="editInfo"
          parent-managed
          :show-background="false"
          @edit="startEdit"
          @save="onSaveInfo"
          @cancel="onCancelEdit">

          <div class="file-info" v-loading="saving" element-loading-text="Saving...">

            <el-alert v-if="saveError" type="error" show-icon :closable="false"
              title="Could not save Additional Info"
              description="Your changes are still here. Fix any highlighted fields and try Save again, or Cancel to discard them." />

            <el-form :model="draft" :rules="infoRules" :ref="infoRef" :disabled="saving" size="small"
              :label-position="isMediumOrLess ? 'top' : 'left'" :label-width="isMediumOrLess ? null : '9em'">

              <div v-if="!editInfo" key="file-info-read">
                <template v-if="hasAnyInfo">

                  <!-- Read mode omits a row entirely when its value is unset, rather than
                       rendering an em-dash placeholder (Carlos, 2026-08-19). Edit mode still
                       shows all four fields, so an omitted row is always reachable via Edit. -->
                  <el-form-item v-if="displayStatus" label="Review Status">
                    <!-- The tooltip wraps the WHOLE tag, not its inner span: nesting it
                         inside would make the ~8px of tag padding a dead zone that never
                         fires the tip. -->
                    <el-tooltip v-if="statusTagTooltip" placement="top" :content="statusTagTooltip">
                      <el-tag :type="statusTagType" size="small" effect="light" :aria-label="'Review status: ' + displayStatus">
                        <i :class="statusTagIcon" aria-hidden="true"></i> {{displayStatus}}
                      </el-tag>
                    </el-tooltip>
                    <el-tag v-else :type="statusTagType" size="small" effect="light" :aria-label="'Review status: ' + displayStatus">
                      <i :class="statusTagIcon" aria-hidden="true"></i> {{displayStatus}}
                    </el-tag>
                  </el-form-item>

                  <el-alert v-if="hasStatusConflict" type="warning" show-icon :closable="false"
                    title="Approval and review status disagree"
                    :description="conflictDescription" />

                  <el-form-item v-if="displayDeposit > 0" label="Security Deposit">
                    <b>{{formatMoney(displayDeposit)}}</b>
                  </el-form-item>

                  <el-form-item v-if="displayRef" label="Ref #">
                    <b>{{displayRef}}</b>
                  </el-form-item>

                  <!-- Divider only earns its place when it separates something above from
                       remarks below; with only one of the two present it is a stray rule. -->
                  <el-divider v-if="hasScalarInfo && hasRemarks" />

                  <!-- XOL-596 (readable remarks): the shared framed block. A long remark scrolls
                       INSIDE the frame (focusable, so a keyboard user can scroll it). -->
                  <read-only-remarks v-if="hasRemarks" label="Remarks" :html="displayRemarks" max-height="12em" />

                </template>

                <p v-else-if="canEditInfo" class="text-muted">No additional info recorded yet. Use Edit to add a review status, security deposit, reference number or remarks.</p>
                <p v-else class="text-muted">No additional info recorded.</p>
              </div>

              <div v-else key="file-info-edit">

                <el-form-item label="Review Status" prop="status">
                  <el-select v-model="draft.status" :key="formKey" placeholder="Select a review status" style="width: 100%">
                    <el-option v-for="s in statusOptions" :key="s" :label="s" :value="s" />
                  </el-select>
                </el-form-item>

                <currency-input label="Security Deposit" prop="deposit" v-model="draft.deposit" :min="0" />

                <el-form-item label="Ref #" prop="ref">
                  <el-input v-model="draft.ref" maxlength="32" placeholder="Reference number" />
                </el-form-item>

                <el-divider />

                <h5 class="remarks-heading">Remarks</h5>
                <html-input v-model="draft.remarks" no-form :height="quillHeight" :show-image="showQuillImage" />

              </div>

            </el-form>
          </div>
        </edit-pane>
      </div>

      <el-row>
        <div v-if="fileData.mimeType && fileData.mimeType.endsWith('pdf')">
          <pdf-viewer :url="getEndpoint('/file/view/' + fileData.url)" />
        </div>

        <div v-else-if="fileData.url">
          <img style="width: 100%;" :src="getEndpoint('/file/view/' + fileData.url)" />
        </div>
      </el-row>

      <!-- XOL-606: Uploaded and Last Download stack, left-aligned, at xs / sm / md; two 12-span
           columns are about 130px each at 375px and the badge and long dates wrap badly. -->
      <el-row align="middle">
        <el-col :span="isMediumOrLess ? 24 : 12">
          <!-- XOL-526 (UX §1.4): legacy rows (no uploadOrgShortName) render
               exactly today's line, unchanged. -->
          <small>Uploaded: <b>{{this.fileData.upload_by}}</b><template v-if="fileData.uploadOrgShortName"> (<org-badge :organisation="{ shortName: fileData.uploadOrgShortName, colourCode: fileData.uploadOrgColourCode }" />)</template> at <b>{{this.formatDateTime(this.fileData.upload_at)}}</b></small>
        </el-col>

        <el-col :span="isMediumOrLess ? 24 : 12" :align="isMediumOrLess ? 'left' : 'right'">
          <small v-if="fileData.downloaded"><small>Last Download: On <b>{{this.formatDateTime(fileData.lastDownload_at)}}</b> by <b>{{fileData.lastDownload_by}}</b></small></small>
        </el-col>
      </el-row>
    </div>
  </el-form>
</template>

<script>

import PostMixins from '@/mixins/PostMixins'
import DateMixins from '@/mixins/DateMixins'
import NumberMixins from '@/mixins/NumberMixins'

import PdfViewer from '@/components/atoms/PdfViewer'
import ReadOnlyRemarks from '@/components/atoms/ReadOnlyRemarks'
import { noticeOutcome, showToast } from '@/lib/requestActivity'

// XOL-451: FileDetails no longer consumes <auto-form> — see F1 in
// docs/plans/2026-08-19-xol-451-file-details-remarks.md. AutoForm.vue is
// untouched and keeps its own spec (AutoForm.spec.js).

export default {

  mixins: [PostMixins, DateMixins, NumberMixins],

  components: {
    PdfViewer,
    ReadOnlyRemarks
  },

  props: {
    rowData: {
      type: Object
    },

    rowIndex: {
      type: Number
    }

  },

  data () {
    return {
      editTitle: false,

      // Legitimate data() latch (XOL-451 step 1): "was this file already
      // approved when the panel opened" must not change once the switch is
      // flipped, or the switch itself would vanish mid-edit. The isTowerCo()
      // half of the old showSwitch snapshot is late-bound via the showSwitch
      // computed below instead.
      wasApprovedOnOpen: !!this.rowData.approved,

      editInfo: false,
      saving: false,
      saveError: false,
      formKey: 0,
      infoRef: 'file-info-form',

      // Placeholder until startEdit()/onCancelEdit() populate a real draft
      // (buildDraft()) — never read while editInfo is false.
      draft: {
        status: null,
        deposit: 0,
        ref: null,
        remarks: ''
      },

      statusOptions: ['Pending', 'Conditional', 'Approved', 'Rejected'],

      infoRules: {
        status: [{ required: true, trigger: 'change', message: 'Select a review status.' }],
        ref: [{ max: 32, trigger: 'blur', message: 'Ref # must be 32 characters or fewer.' }],
        deposit: [{ validator: this.depositValidator, trigger: 'blur' }]
      }
    }
  },

  computed: {
    // Async-store snapshots (CLAUDE.md: don't snapshot store values in data()).
    fileData () {
      return this.rowData
    },

    disableForm () {
      return this.rowData && this.rowData.options ? this.rowData.options.disableForm : false
    },

    // XOL-526: threaded from RequestWorkflow's `<files-table :upload-locked>`
    // through FilesTable's `detailProps` (see FilesTable.vue). True once the
    // party-1 step for this file's step is complete — the API refuses a
    // counterparty's removal in that state, same as it refuses the upload.
    // XOL-596 (Carlos, 2026-10-14): it now means "this step's files are frozen" — for
    // every viewer, the Tower Co included: no Remove, rename, approval switch,
    // Additional Info Edit or Remove Category (the API refuses them all).
    uploadLocked () {
      return this.rowData && this.rowData.options ? !!this.rowData.options.uploadLocked : false
    },

    showSwitch () {
      return this.isTowerCo() && !this.wasApprovedOnOpen
    },

    thisRef () {
      return 'file-details-' + this.fileData.id
    },

    // -- Normalising read layer (XOL-451 step 3) --------------------------

    fileState () {
      return this.fileData.state && typeof this.fileData.state === 'object' ? this.fileData.state : {}
    },

    displayStatus () {
      return this.normaliseStatus(this.fileState.status)
    },

    displayDeposit () {
      return Number(this.fileState.deposit) || 0
    },

    displayRef () {
      return this.fileData.ref || this.fileState.ref || null
    },

    displayRemarks () {
      return this.fileData.remarks
    },

    hasRemarks () {
      return this.hasRichText(this.displayRemarks)
    },

    // Narrow-row guard (F4/D5): the two dashboard "Recent Files" tables
    // select a reduced column set that has no `state` key at all.
    hasDetailFields () {
      return Object.prototype.hasOwnProperty.call(this.rowData, 'state')
    },

    canEditInfo () {
      return this.isTowerCo() && (this.isManager() || this.isApprover()) &&
        !this.isViewOnly() && !this.disableForm && !this.uploadLocked && this.hasDetailFields
    },

    // XOL-526 (D6, D11): is the VIEWER's own org the uploader? A legacy row
    // (upload_org_id null) is never "mine" — only the Tower Co may remove it.
    uploadedByMyOrg () {
      var row = this.fileData
      var me = this.org()
      return !!(row && row.upload_org_id !== null && row.upload_org_id !== undefined &&
        me && me.id !== null && me.id !== undefined &&
        Number(row.upload_org_id) === Number(me.id))
    },

    // XOL-526 (UX §1.5.1): explains a HIDDEN Remove button to a non-Tower-Co
    // viewer. Never shown to the Tower Co, and never when the file is
    // approved (the existing rule already hides Remove there with no note).
    removeNote () {
      if (this.isTowerCo() || this.fileData.approved) {
        return null
      }

      if (this.uploadedByMyOrg) {
        if (this.fileData.downloaded) {
          return 'This file has been downloaded, so only a ' + this.towerCoName() + ' manager can remove it. Contact them if it needs to be replaced.'
        }
        return null
      }

      if (this.fileData.upload_org_id === null || this.fileData.upload_org_id === undefined) {
        return 'Only ' + this.towerCoName() + ' can remove this file.'
      }

      // XOL-526 (reviewer correction 2026-09-23): upload_org_id can be set
      // (attribution exists) while uploadOrgShortName is still null/undefined
      // — fall back rather than render "Only null or {Tower Co} ...".
      var otherOrg = this.fileData.uploadOrgShortName || 'another organisation'
      return 'Only ' + otherOrg + ' or ' + this.towerCoName() + ' can remove this file.'
    },

    // Any of the three scalar rows present. Drives the divider, which should only
    // appear when it actually separates scalars above from remarks below.
    hasScalarInfo () {
      return !!(this.displayStatus || this.displayDeposit > 0 || this.displayRef)
    },

    hasAnyInfo () {
      return this.hasScalarInfo || this.hasRemarks
    },

    // Conflict A: approved above, but status says Pending/Rejected.
    // Conflict B: not approved above, but status says Approved.
    // Conditional (either approval value) and null/unrecognised are coherent.
    hasStatusConflict () {
      var approved = !!this.fileData.approved
      var status = this.displayStatus

      if (approved && (status === 'Pending' || status === 'Rejected')) {
        return true
      }

      if (!approved && status === 'Approved') {
        return true
      }

      return false
    },

    conflictDescription () {
      if (!this.hasStatusConflict) {
        return ''
      }

      if (this.fileData.approved) {
        return this.canEditInfo
          ? 'This file is marked Approved above, but its review status is "' + this.displayStatus + '". One of the two is out of date — use Edit to correct the review status.'
          : 'This file is marked Approved above, but its review status is "' + this.displayStatus + '". One of the two is out of date — ask a manager to correct it.'
      }

      return this.canEditInfo
        ? 'The review status is "Approved", but this file has not been approved above. Use Edit to correct the review status, or approve the file.'
        : 'The review status is "Approved", but this file has not been approved above. Ask a manager to correct it.'
    },

    statusTagType () {
      switch (this.displayStatus) {
        case 'Approved': return 'success'
        case 'Conditional': return 'warning'
        case 'Pending': return 'info'
        case 'Rejected': return 'danger'
        default: return 'info'
      }
    },

    statusTagIcon () {
      switch (this.displayStatus) {
        case 'Approved': return 'fa fa-check-circle'
        case 'Conditional': return 'fa fa-exclamation-circle'
        case 'Pending': return 'fa fa-hourglass-half'
        case 'Rejected': return 'fa fa-times-circle'
        default: return 'fa fa-question-circle'
      }
    },

    statusTagTooltip () {
      if (this.displayStatus === 'Conditional') {
        return 'Approved with conditions — see Remarks.'
      }

      if (this.displayStatus && this.statusOptions.indexOf(this.displayStatus) === -1) {
        return 'Unrecognised status value stored on this file.'
      }

      return null
    },

    quillHeight () {
      return this.isMediumOrLess ? 176 : 222
    },

    showQuillImage () {
      return !(this.isXSmall || this.isSmall)
    }
  },

  methods: {
    edit () {
      this.editTitle = !this.editTitle

      if (!this.editTitle) {
        this.saveTitle()
      }
    },

    // XOL-451 defect 6: the old edit() -> save() with no argument set
    // this.fileData.state = undefined, wiping the in-memory state blob (the
    // red "Rejected" Download button reverted until the table refreshed).
    // A dedicated, minimal payload never touches state.
    saveTitle () {
      this.saveForm(this, '/file', this.fileData.id, { title: this.fileData.title },
        'File Details', this.titleSaved, this.titleError, false, null, true)
    },

    titleSaved () {
      this.$message({ message: 'Category saved: [OK]', type: 'success' })
    },

    titleError (err) {
      // send() has already notified the user; nothing else to do here.
      // A completed step (XOL-596): the request drawer says why.
      this.stepCompletedRefusal(err, 'categoryRename', this.completedScope())
    },

    approved (value) {
      if (value) {
        this.fileData.approved_at = this.formatDateTimeSql(new Date())
        this.fileData.approved_by = this.userName()

        // `approvedFailed` puts the switch back (a refusal must not look saved).
        // XOL-586 (XOL-590): the API e-mails the owner (and an RTB's provider) itself when the
        // file is approved; its response says what it did, `notice`, and `approvedSaved` shows
        // it. This save raises no toast of its own (showMessage false), so that is the one.
        this.saveForm(this, '/file', this.fileData.id, this.fileData, 'File Details', this.approvedSaved, this.approvedFailed, false, null, true)

      } else {
        this.fileData.approved_at = null
        this.fileData.approved_by = null
      }
    },

    // The approval is saved: say what happened to its e-mail (the response's `notice`: one
    // toast). Delta 1 (item 1): when there is no mail toast to show (Send E-mails is off, or the
    // file is not in a request) the save still confirms itself with the generic message that
    // saveForm would have shown (`updatedMessage('File Details')`, PostMixins `send()`), because this
    // save passes showMessage = false.
    approvedSaved (data) {
      var key = this.fileData && this.fileData.processKey ? String(this.fileData.processKey).toUpperCase() : null
      var toast = noticeOutcome(data ? data.notice : null, { kind: 'file_approved', rtx: key })

      if (!toast) {
        this.$message({ type: 'success', title: 'Success', message: this.updatedMessage('File Details') })
        return
      }

      showToast(this, toast)
    },

    // Which request this file belongs to, for a 403 step_completed (several request drawers
    // can be mounted: only the matching one reports it).
    completedScope () {
      return { actionId: this.fileData.process_id }
    },

    // The approval was refused (a completed step, XOL-596): the switch goes back to what
    // the file was, and the request drawer says why. Any other failure keeps today's
    // behaviour (send() already toasted; the switch stays as it was left).
    approvedFailed (err) {
      if (this.stepCompletedRefusal(err, 'fileApprove', this.completedScope())) {
        this.fileData.approved = false
        this.fileData.approved_at = null
        this.fileData.approved_by = null
      }
    },

    deleted (response) {
      this.$parent.hideDetailRow(this.rowData.id)
      this.$parent.refresh()
    },

    // XOL-526: FilesTable keeps a per-row el-upload model (`fileUploads`) so
    // a re-mounted FileUpload (the v-if flips back to "Upload" once this
    // row's url is nulled below) doesn't inherit the file we just removed —
    // without this, the upload button stayed disabled until a page reload.
    // `rowData.options` / the callback are both optional: the narrow
    // dashboard "Recent Files" tables never set `:options` at all.
    notifyFileRemoved () {
      if (this.rowData && this.rowData.options && this.rowData.options.onFileRemoved) {
        this.rowData.options.onFileRemoved(this.fileData.id)
      }
    },

    remove () {
      // XOL-526 (UX §1.5.1): showMessage=false + fileRemoved() replaces the
      // generic "[{name}] updated" toast with "Removed {name}" — capture the
      // name now, since `deleted()` may already have torn the row down by
      // the time the response comes back.
      var name = this.fileData.name
      this.saveForm(this, '/file/delete', this.fileData.id, this.fileData, name,
        this.fileRemoved.bind(this, name), this.removeFailed, false, null, true)
    },

    removeFailed (err) {
      // A completed step (XOL-596): the request drawer says why; the file stays.
      this.stepCompletedRefusal(err, 'fileRemove', this.completedScope())
    },

    fileRemoved (name, response) {
      this.notifyFileRemoved()
      this.$message({ type: 'success', message: 'Removed ' + name })
      this.deleted(response)
    },

    // Remove Category only shows when the slot already has no file (see the
    // template guard), so there is no slot left to re-upload into — unlike
    // remove()/fileRemoved(), this path deliberately does not call
    // notifyFileRemoved().
    deleteFile () {
      this.deleteItem(this, this.fileData.id, '/files', 'Remove category: "' + this.fileData.title + '"', this.deleted,
        true, null, 'Yes', { what: 'categoryRemove', scope: this.completedScope() })
    },

    // -- Additional Info panel (XOL-451) -----------------------------------

    normaliseStatus (value) {
      if (!value) {
        return null
      }

      if (value === 'Condtional') {
        return 'Conditional'
      }

      return value
    },

    // Quill emits '<p><br></p>' (and similar) for a "cleared" editor — see
    // HtmlInput.vue's commented-out tag-stripping guard. Strip tags/&nbsp;
    // and test for real content so a blank paragraph doesn't read as remarks.
    hasRichText (html) {
      if (!html) {
        return false
      }

      var text = String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, '').trim()
      return text.length > 0
    },

    // A fresh object every time — never an alias of fileData.state — and
    // always fully populated so every key is reactive (defect 8, defect 14).
    buildDraft () {
      return {
        status: this.displayStatus,
        deposit: this.displayDeposit,
        ref: this.displayRef,
        remarks: this.hasRemarks ? this.displayRemarks : ''
      }
    },

    startEdit () {
      this.saveError = false
      this.draft = this.buildDraft()
      this.editInfo = true
    },

    onCancelEdit () {
      this.draft = this.buildDraft()
      this.saveError = false
      this.editInfo = false
      this.formKey++
    },

    onSaveInfo () {
      // EditPane's Save/Cancel buttons sit outside the v-loading mask, so a
      // double-click is otherwise a double PUT.
      if (this.saving) {
        return
      }

      this.saveError = false

      var form = this.$refs[this.infoRef]
      if (!form) {
        return
      }

      form.validate(function (valid) {
        if (!valid) {
          return
        }

        this.putInfo()
      }.bind(this))
    },

    // Minimal payload: remarks/ref at the top level (real columns), status/
    // deposit folded into a CLONE of the existing state blob so unknown
    // legacy keys (e.g. state.emails) survive. ref heals out of state on
    // every save, per D1.
    buildInfoPayload () {
      var state = Object.assign({}, this.fileState)
      state.status = this.draft.status
      state.deposit = Number(this.draft.deposit) || 0
      delete state.ref

      return {
        remarks: this.hasRichText(this.draft.remarks) ? this.draft.remarks : null,
        ref: this.draft.ref,
        state: state
      }
    },

    putInfo () {
      // send() hard-blocks view-only writes and returns with no callback —
      // without this guard `saving` would hang forever for a view-only user
      // who somehow reaches Save.
      if (this.isViewOnly()) {
        return
      }

      this.saving = true

      this.saveForm(this, '/file', this.fileData.id, this.buildInfoPayload(),
        'File Details', this.infoSaved, this.infoError, false, null, true)
    },

    // Commit-from-response only — never optimistic (D3/defect 8).
    infoSaved (data) {
      this.saving = false

      if (data && typeof data === 'object') {
        this.fileData.remarks = data.remarks
        this.fileData.ref = data.ref
        this.fileData.state = data.state
        this.fileData.updated_by = data.updated_by
        this.fileData.updated_at = data.updated_at
      }

      this.editInfo = false
      this.formKey++
      this.$message({ message: 'Additional info saved: [OK]', type: 'success' })
    },

    infoError (err) {
      this.saving = false
      this.saveError = true
      // send() has already raised a $notify for this failure — no second toast.
      // A completed step (XOL-596): send() raised none; the request drawer says why.
      this.stepCompletedRefusal(err, 'fileInfo', this.completedScope())
    },

    // type: 'number' alone is wrong here: CurrencyInput can round an empty
    // field to a non-numeric value, and a naive type check lets NaN through.
    depositValidator (rule, value, callback) {
      if (value === null || value === undefined || value === '') {
        callback()
        return
      }

      var n = Number(value)

      if (isNaN(n) || !isFinite(n)) {
        callback(new Error('Enter a valid security deposit amount.'))
        return
      }

      if (n < 0) {
        callback(new Error('Security deposit cannot be negative.'))
        return
      }

      callback()
    }
  }
}

</script>

<style scoped>
 .file-details {padding: 1em; background-color: #F2F6FC;}

 /* XOL-606: contain the detail's width. FileDetails is a vuetable detail row: a <td colspan>
    in a raw <table class="el-table"> with AUTO table layout, where a cell's min-content width
    is a floor for the TABLE (width: 100% / max-width: 100% do not cap it) and
    `overflow-wrap: break-word` does not lower min-content. So ONE long token (a URL in
    Remarks, a long file name, a long Ref #) made the whole table wider than the drawer, and
    `.el-tabs__content { overflow: hidden }` then clipped its right side (the PDF preview
    with it). A single `minmax(0, 1fr)` track makes this form's min-content 0, so the table
    keeps its column width and long tokens wrap inside it. The root form has exactly one
    child (the `v-if` / `v-else` div), so nothing else about the flow changes. Keep `inline`
    on the root form (XOL-451 note below). */
 .file-details {display: grid; grid-template-columns: minmax(0, 1fr);}

 /* With the width contained, a long unbroken file name would overflow its own box. */
 .file-name {overflow-wrap: anywhere;}
 a {text-decoration: none;}

 /* XOL-596: the divider above already separates the remarks block, so it drops the
    section's own margin. */
 .file-info .remarks-section { margin: 0; }
 .file-info .el-divider--horizontal { margin: 0.9em 0; }
 .file-info h5 { margin: 0 0 0.528em 0; }

 /* Separate the panel from the Approval Status row above it, which is the last
    row of the root inline form and carries no bottom margin of its own. */
 .file-info-pane { margin-top: 1.4em; }

 /* XOL-451: Element's `.el-form--inline .el-form-item` is a DESCENDANT selector,
    so the root <el-form ... inline> of this component (line 2) leaks into this
    nested, deliberately non-inline panel form and lays Review Status / Security
    Deposit / Ref # out on a single line with the values dangling far right.
    Re-assert block layout locally. Verified in-browser: without this the three
    scalars collapse onto one row. Do NOT drop the `inline` on the root form —
    the Category / File name / Approval rows depend on it. */
 .file-info .el-form-item {
   display: block;
   margin-right: 0;
   vertical-align: baseline;
 }

 /* Same leak on the form-item internals: `.el-form--inline` also forces
    `__label` to `display:inline-block; float:none` and `__content` to
    `inline-block`, which collapses the control column to its content width and
    makes a wrapped validation message overrun the divider below it. Element's
    own escape hatch (`.el-form--inline.el-form--label-top .el-form-item__content`)
    needs BOTH classes on the SAME element, which never happens here — `--inline`
    is on the root form, `--label-top` on this nested one — so it never fires.
    `__label`/`__content` are internal to ElFormItem's template and carry no
    scope id, so a plain scoped selector cannot reach them: `>>>` is required
    (same form as BulkRateChange.vue:756). */
 .file-info >>> .el-form-item__content { display: block; }
 .file-info >>> .el-form-item__label { float: left; display: block; }
</style>
